﻿import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { useQueryClient } from "react-query";
import { useToasts } from "react-toast-notifications";
import { useNavigate, useSearchParams } from "react-router-dom";

import BasePagination from "@/components/Pagination/BasePagination";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import CustomTable from "@/components/Table/ReactTable/CustomTable";
import Fetching from "@/components/Fetching/Fetching";
import Head from "@/components/Content/Head";
import Detail from "@/components/Detail/Detail";
import BasicConfirm from "@/components/Confirm/BasicConfirm";

import { hasPermission, validRoles } from "@/services/Utils";

import { useApplicationOrdersPaginate, useAppOrderDuplicate } from "@/hooks/planning/application-order/applicationOrders";

import { baseFilters } from "./orderApplication.constants";
import AppOrdersFilter from "./OrderApplicationFilter";
import AppOrdersAction from "./Actions/OrderApplicationAction";
import AppOrdersActionSelect from "./Actions/OrderApplicationActionSelect";

import { useOrderApplicationColumns } from "./hooks/useOrderApplicationColumns";
import { useOrderApplicationFilters } from "./hooks/useOrderApplicationFilters";
import { useOrderApplicationSelection } from "./hooks/useOrderApplicationSelection";

import classes from "./orderApplication.module.scss";

const AppOrders = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const { addToast } = useToasts();
	const queryClient = useQueryClient();

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola"]);

	const { filter } = useSelector((state) => state.auth);
	const { customer } = useSelector((state) => state.auth.user);
	const { estate_parent_id, estate_id, search, parameter } = filter;

	const [showFilter, setShowFilter] = useState(false);
	const [titleDetail, setTitleDetail] = useState("");
	const [itemsDetail, setItemsDetail] = useState([]);
	const [showDetail, setShowDetail] = useState(false);
	const [showCopyOA, setShowCopyOA] = useState(false);

	const { selectedRef, toggleSelect, clearSelection, getSelectedIds } = useOrderApplicationSelection();

	const initialBase = {
		...baseFilters,
		estate_parent_id,
		estate_id,
		search,
		parameter,
		...(searchParams.get("type") && { typeFilter: searchParams.get("type") }),
		...(searchParams.get("status") && { appOrdersStateFilter: searchParams.get("status") }),
	};

	const { base, setBase, setPage, setPaginate, applyFilter } = useOrderApplicationFilters(initialBase);

	const { mutate: duplicate, isLoading: loadDuplicate } = useAppOrderDuplicate(base);
	const { data: response, isLoading: loading, isFetching } = useApplicationOrdersPaginate(base);

	const { list, permissions, business, states, types } = response || {};
	const { last_page, total, from, to, data } = list || {};
	const isInitialLoading = loading && !response;
	const isListRefetching = isFetching && !!response && !loadDuplicate;

	useEffect(() => {
		setBase((prevBase) => ({
			...prevBase,
			estate_id,
			search,
			parameter,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [estate_id, search, parameter]);

	const setData = (newData) => {
		const oldCache = queryClient.getQueryData(["application_orders", { ...base }]);
		if (!oldCache?.list) {
			return;
		}

		const newCache = {
			...oldCache,
			list: {
				...oldCache.list,
				data: newData,
			},
		};
		queryClient.setQueryData(["application_orders", { ...base }], newCache);
	};

	const handleShow = (title, items) => {
		setTitleDetail(title);
		setItemsDetail(items);
		setShowDetail(true);
	};

	const handleDuplicate = () => {
		const request = {
			base,
			element: getSelectedIds(),
		};

		duplicate(request, {
			onSuccess: () => {
				addToast(`Ordenes de Aplicación copiadas correctamente`, {
					appearance: "success",
				});
				clearSelection();
				setShowCopyOA(false);
			},
		});
	};

	const handleCalculate = () => {
		navigate("/ordenes_aplicacion?oa=" + getSelectedIds().join(",") + "&action=calcular_necesidad");
	};

	// Columnas usando el hook
	const COLUMNS = useOrderApplicationColumns({
		customer,
		data,
		setData,
		base,
		selectedRef,
		toggleSelect,
		handleShow,
		canShowPrice: isManager,
	});

	if (isInitialLoading || loadDuplicate) {
		return <Fetching />;
	}

	return (
		<>
			<Head title={"Órdenes de Aplicación"} />

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			<Row className={classes.purchase_global}>
				{showFilter && <AppOrdersFilter applyFilter={applyFilter} filters={base} business={business} states={states} types={types} />}
				<Col md={showFilter ? 10 : 12} className={classes.purchase_content}>
					<AppOrdersAction showFilter={showFilter} setShowFilter={setShowFilter} filters={base} permissions={permissions} />

					{data && selectedRef.current.size !== 0 && <AppOrdersActionSelect setShowCopyOA={setShowCopyOA} handleCalculate={handleCalculate} />}

					{isListRefetching ? (
						<Fetching topMargin="4em" message="Cargando órdenes de aplicación..." />
					) : (
						<>
							{hasPermission("show", permissions) && data && data.length > 0 && (
								<CustomTable COLUMNS={COLUMNS} data={data} base={base} setBase={setBase} colorBackground={false} />
							)}
							<BasePagination
								page={base.page}
								paginate={base.paginate}
								last_page={last_page}
								quantity={total}
								from={from}
								to={to}
								setPage={setPage}
								setPaginate={setPaginate}
							/>
						</>
					)}
				</Col>
			</Row>

			<Detail title={titleDetail} items={itemsDetail} isOpen={showDetail} handleClose={setShowDetail} />

			<BasicConfirm
				showConfirm={showCopyOA}
				setShowConfirm={setShowCopyOA}
				handleAction={handleDuplicate}
				message="¿Está seguro que desea copiar las ordenes seleccionadas?"
			/>
		</>
	);
};

export default AppOrders;
